import { Component, Input, OnInit, ElementRef, HostListener, } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ContentService } from '../services/content.service';
import { LocaleService } from '../services/locale.service';
import { filter } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from '../shared/confirm-modal/confirm-modal.component';

type LanguageOption = { label: string; value: string };

const languageSwitcherCopy = {
  en: {
    title: 'Change questionnaire language?',
    message: (language: string) =>
      `Switch the questionnaire language to <strong>${language}</strong>? The page will reload so translated questions can be loaded.`,
    confirmButtonText: 'Switch language',
    cancelButtonText: 'Cancel',
  },
  es: {
    title: '¿Cambiar el idioma del cuestionario?',
    message: (language: string) =>
      `¿Cambiar el idioma del cuestionario a <strong>${language}</strong>? La página se recargará para cargar las preguntas traducidas.`,
    confirmButtonText: 'Cambiar idioma',
    cancelButtonText: 'Cancelar',
  },
};

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit {
  @Input() logoLink: string | null = null;
  @Input() isDarkNavbar = false;

  userName = '';
  userEmail = '';
  userRole = '';
  userImage: string | null = null;
  isLoggedIn = false;
  showDropdown = false;
  showLanguageSwitcher = false;
  selectedLocale = 'en';
  isSavingLocale = false;
  languageOptions: LanguageOption[] = [
    { label: 'English', value: 'en' },
    { label: 'Español', value: 'es' },
  ];

  constructor(
    private authService: AuthService,
    private contentService: ContentService,
    private localeService: LocaleService,
    private router: Router,
    private eRef: ElementRef,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.isLoggedIn = this.authService.areTokensValid();
    const user = this.authService.getCurrentUser() as any;
    this.fetchProfile(user);
    this.selectedLocale = this.localeService.currentLocale;
    this.updateLanguageSwitcherVisibility(this.router.url);

    // Subscribe to updates
    this.contentService.profileUpdated$.subscribe(() => {
      this.fetchProfile(user);
    });

    this.localeService.locale$.subscribe((locale) => {
      this.selectedLocale = locale;
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateLanguageSwitcherVisibility(event.urlAfterRedirects);
      });
  }

  private fetchProfile(user: any): void {
    if (!user.name) {
      this.contentService.getDonorProfile().subscribe({
        next: (donor_info) => {
          const firstName = donor_info.data.donor.firstName?.trim();
          const lastName = donor_info.data.donor.lastName?.trim();
          const userId = donor_info.data.donor.userId;

          if (firstName || lastName) {
            this.userName = `${firstName || ''} ${lastName || ''}`.trim();
          } else {
            this.userName = '';
          }

          this.userEmail = userId ?? '';
          this.userImage = null;
          this.userRole = '';
        },
        error: (error) => {
          console.error('Error fetching donor profile', error);
        }
      });
    } else {
      this.userName = user.name ?? '';
      this.userEmail = user.email ?? '';
      this.userImage = user.image?.trim() || null;
      this.userRole = user.role ?? '';
    }
  }

  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  navigateToLogoLink(): void {
    if (this.logoLink) {
      this.router.navigate([this.logoLink]);
    }
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  get selectedLanguageCode(): string {
    return this.selectedLocale.toUpperCase();
  }

  get languageSwitcherLabel(): string {
    return 'Language';
  }

  get nextLocale(): string {
    return this.selectedLocale === 'es' ? 'en' : 'es';
  }

  get nextLanguageLabel(): string {
    return (
      this.languageOptions.find((language) => language.value === this.nextLocale)
        ?.label || this.nextLocale.toUpperCase()
    );
  }

  private get copy() {
    return (
      languageSwitcherCopy[
        this.selectedLocale as keyof typeof languageSwitcherCopy
      ] || languageSwitcherCopy.en
    );
  }

  confirmLanguageChange(locale: string): void {
    if (locale === this.selectedLocale || this.isSavingLocale) {
      return;
    }

    const modalRef = this.modalService.open(ConfirmModalComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = this.copy.title;
    modalRef.componentInstance.message = this.copy.message(
      this.nextLanguageLabel
    );
    modalRef.componentInstance.actionType = 'confirm';
    modalRef.componentInstance.confirmButtonText = this.copy.confirmButtonText;
    modalRef.componentInstance.cancelButtonText = this.copy.cancelButtonText;

    modalRef.result
      .then(() => this.onLanguageChange(locale))
      .catch(() => {});
  }

  onLanguageChange(locale: string): void {
    if (locale === this.selectedLocale || this.isSavingLocale) {
      return;
    }

    this.isSavingLocale = true;
    this.localeService.setLocale(locale);

    this.contentService.updateDonorLocale(locale).subscribe({
      next: () => {
        if (this.shouldReloadAfterLocaleChange()) {
          window.location.reload();
          return;
        }

        this.isSavingLocale = false;
      },
      error: (err) => {
        console.error('Failed to save preferred questionnaire language', err);
        this.isSavingLocale = false;
      },
    });
  }

  private shouldReloadAfterLocaleChange(): boolean {
    const currentUrl = this.router.url;
    return (
      currentUrl.startsWith('/questionnaire/iquestionnaire') ||
      currentUrl.startsWith('/questionnaire/cquestionnaire') ||
      currentUrl.startsWith('/questionnaire/postinitialineligible')
    );
  }

  private updateLanguageSwitcherVisibility(url: string): void {
    this.showLanguageSwitcher =
      url.startsWith('/questionnaire') &&
      !url.startsWith('/questionnaire/preinitial');
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  getInitials(): string {
    if (this.userName.trim()) {
      return this.userName
        .trim()
        .split(/\s+/)
        .map(word => word[0])
        .join('')
        .toUpperCase();
    }

    return this.userEmail ? this.userEmail[0].toUpperCase() : '';
  }
}
